import React, { Component } from "react";
import axios from "../../config/axios";
import { Store } from "react-notifications-component";
import {
  Upload, FileText, Eye, Trash2, CheckCircle2, XCircle,
} from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";
import confirmAlert from "../../utils/confirmAlert";
import { storageUrl } from "../../utils/storageUrl";

import {
  PageHeader,
  Button,
  IconButton,
  Tooltip,
  DataTable,
  EmptyState,
  Card,
  StatusIndicator,
} from "../../design-system";

function formatFileSize(bytes) {
  if (!bytes) return "N/A";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " ko";
  return (bytes / (1024 * 1024)).toFixed(1) + " Mo";
}

class CVsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cvs: [], loading: true, uploading: false, selectedFile: null,
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.loadCVs();
  }

  loadCVs = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.get("/api/cvs");
      this.setState({ cvs: response.data.cvs || [], loading: false });
    } catch (error) {
      console.error("Error loading CVs:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Impossible de charger les CVs",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      Store.addNotification({
        title: "Erreur", message: "Seuls les fichiers PDF sont autorisés",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      Store.addNotification({
        title: "Erreur", message: "Le fichier est trop volumineux. Taille maximum : 10 Mo",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      return;
    }
    this.setState({ selectedFile: file });
  };

  handleUpload = async () => {
    const { selectedFile } = this.state;
    if (!selectedFile) return;
    this.setState({ uploading: true });
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const formData = new FormData();
      formData.append("cv", selectedFile);
      const response = await axios.post("/api/cvs", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Store.addNotification({
        title: "CV uploadé",
        message: response.data.message || "Le CV est disponible.",
        type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      this.setState({ selectedFile: null, uploading: false });
      if (this.fileInputRef.current) this.fileInputRef.current.value = "";
      this.loadCVs();
    } catch (error) {
      console.error("Error uploading CV:", error);
      this.setState({ uploading: false });
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Erreur lors de l'upload",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  handleToggleActive = async (cv) => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const url = cv.isActive ? `/api/cvs/${cv.id}/deactivate` : `/api/cvs/${cv.id}/activate`;
      const response = await axios.put(url);
      Store.addNotification({
        title: cv.isActive ? "CV désactivé" : "CV activé",
        message: response.data.message,
        type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      this.loadCVs();
    } catch (error) {
      console.error("Error toggling CV:", error);
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Erreur",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  handleDelete = (cv) => {
    confirmAlert({
      title: "Supprimer le CV",
      message: `« ${cv.filename} » sera supprimé définitivement.`,
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("jwtToken");
          if (token) setAuthToken(token);
          await axios.delete(`/api/cvs/${cv.id}`);
          Store.addNotification({
            title: "CV supprimé",
            type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
          this.loadCVs();
        } catch (error) {
          console.error("Error deleting CV:", error);
          Store.addNotification({
            title: "Erreur",
            message: error.response?.data?.message || "Erreur lors de la suppression",
            type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
        }
      },
    });
  };

  buildColumns() {
    return [
      {
        key: "filename", header: "Fichier", sortable: true,
        render: (row) => (
          <div className="flex items-center gap-2.5">
            <FileText className="w-4 h-4 text-mute shrink-0" strokeWidth={1.6} />
            <span className="font-semibold text-text">{row.filename}</span>
          </div>
        ),
      },
      {
        key: "fileSize", header: "Taille", align: "right", width: 100,
        render: (row) => (
          <span className="font-mono tabular-nums text-sm text-mute">{formatFileSize(row.fileSize)}</span>
        ),
      },
      {
        key: "isActive", header: "Statut", width: 120,
        render: (row) => row.isActive
          ? <StatusIndicator tone="ink">Actif</StatusIndicator>
          : <StatusIndicator tone="muted">Inactif</StatusIndicator>,
      },
      {
        key: "createdAt", header: "Uploadé le", sortable: true, width: 160,
        render: (row) => (
          <span className="font-mono text-xs text-mute">
            {row.createdAt ? new Date(row.createdAt).toLocaleDateString("fr-FR", {
              year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
            }) : "—"}
          </span>
        ),
      },
      {
        key: "actions", header: "", width: 150,
        render: (row) => (
          <div className="flex items-center gap-0.5 justify-end" data-no-row-click>
            <Tooltip content={row.isActive ? "Désactiver" : "Activer"}>
              <IconButton
                size="sm"
                variant="ghost"
                icon={row.isActive ? <XCircle strokeWidth={1.8} /> : <CheckCircle2 strokeWidth={1.8} />}
                aria-label={row.isActive ? `Désactiver ${row.filename}` : `Activer ${row.filename}`}
                onClick={() => this.handleToggleActive(row)}
              />
            </Tooltip>
            <Tooltip content="Voir">
              <IconButton
                as="a"
                href={row.url || storageUrl(row.filepath)}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                variant="ghost"
                icon={<Eye strokeWidth={1.8} />}
                aria-label={`Voir ${row.filename}`}
              />
            </Tooltip>
            {!row.isActive && (
              <Tooltip content="Supprimer">
                <IconButton
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 strokeWidth={1.8} />}
                  aria-label={`Supprimer ${row.filename}`}
                  onClick={() => this.handleDelete(row)}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ];
  }

  render() {
    const { cvs, loading, uploading, selectedFile } = this.state;

    return (
      <AdminPageShell pageTitle="CV">
        <div className="space-y-6 lg:space-y-7">
        <PageHeader
          size="hero"
          eyebrow="— Section viii · CV"
          title={<>Le PDF qu'on <span className="font-serif italic font-normal align-baseline">télécharge.</span></>}
          description="Versions PDF de ton CV. Un seul est actif à la fois — c'est celui qui est téléchargé depuis le site."
          actions={
            <div className="flex items-center gap-2">
              <input
                ref={this.fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={this.handleFileSelect}
                className="sr-only"
              />
              {selectedFile && (
                <span className="text-xs text-mute mr-1 font-mono">
                  {selectedFile.name} <span className="text-mute-2">({formatFileSize(selectedFile.size)})</span>
                </span>
              )}
              <Button
                variant="secondary"
                leftIcon={<Upload className="w-4 h-4" strokeWidth={1.8} />}
                onClick={() => this.fileInputRef.current?.click()}
              >
                Choisir un fichier
              </Button>
              {selectedFile && (
                <Button
                  onClick={this.handleUpload}
                  isLoading={uploading}
                  leftIcon={<CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />}
                >
                  {uploading ? "Upload…" : "Uploader"}
                </Button>
              )}
            </div>
          }
        />

        {!loading && cvs.length === 0 ? (
          <Card>
            <EmptyState
              icon={FileText}
              title="Aucun CV"
              description="Uploade ton premier CV en PDF (10 Mo max)."
              action={
                <Button
                  leftIcon={<Upload className="w-4 h-4" />}
                  onClick={() => this.fileInputRef.current?.click()}
                >
                  Choisir un fichier
                </Button>
              }
            />
          </Card>
        ) : (
          <DataTable
            data={cvs}
            columns={this.buildColumns()}
            rowKey="id"
            isLoading={loading}
            variant="duotone"
            showRowIndex
          />
        )}
        </div>
      </AdminPageShell>
    );
  }
}

export default CVsList;
